import React, { useEffect, useState } from "react";
import { useInventoryReportQuery } from "../../../Store/Apis/reportApi";
import downloadPDF from "../../../Hooks/downloadReportPdf";
import { Link } from "react-router-dom";
import formatDate from "../../../Hooks/formatDate";
import { useSelector } from "react-redux";
import { FaRegFilePdf, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";

// Helper function to get formatted date (YYYY-MM-DD)
const getFormattedDate = (date) => date.toISOString().split("T")[0];

const Inventory = () => {
  const user = useSelector((state) => state.auth.user);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [startDate, setStartDate] = useState(getFormattedDate(yesterday));
  const [endDate, setEndDate] = useState(getFormattedDate(today));
  const [outletId, setOutletId] = useState();
  // outletID: outletId
  const {
    data: Report,
    isSuccess,
    isLoading,
    isFetching,
    isError,
  } = useInventoryReportQuery(
    { startDate, endDate, outletID: outletId },
    { skip: !startDate }
  );

  // PAGINATION CODE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Array.isArray(Report?.data)
    ? Math.ceil(Report.data.length / itemsPerPage)
    : 0;
  const paginatedData = Array.isArray(Report?.data)
    ? Report.data.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      )
    : [];

  const renderPagination = () => {
    const pages = [];
    const pageLimit = 5;
    const startPage = Math.max(2, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    if (totalPages > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${1 === currentPage ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );

      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <li
            key={i}
            className={`page-item ${i === currentPage ? "active" : ""}`}
          >
            <button className="page-link" onClick={() => handlePageChange(i)}>
              {i}
            </button>
          </li>
        );
      }

      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }

      pages.push(
        <li
          key={totalPages}
          className={`page-item ${totalPages === currentPage ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pages;
  };

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setOutletId(user.outletID);
    }
  }, []);

  const rows =
    Array.isArray(Report?.data) &&
    Report.data.map((item, index) => [
      index + 1,
      item.parentName,
      item.serviceTypeName,
      item.openingBalance,
      item.quantityReceived,
      item.quantityIssued,
      item.closingBalance,
      item.reorderLevel,
    ]);
  const columns = [
    "Sl No.",
    "Service Name",
    "Stock Item",
    "Opening Balance",
    "Quantity Received",
    "Quantity Issued",
    "Closing Balance",
    "Re-order Level",
  ];

  const downloadExcel = () => {
    if (!Report?.data || !Array.isArray(Report.data)) {
      alert("No data available for download");
      return;
    }

    // Prepare data for Excel
    const excelData = Report.data.map((item, index) => ({
      "Sl No.": index + 1,
      "Service Name": item.parentName || "",
      "Stock Item": item.serviceTypeName || "",
      "Opening Balance": item.openingBalance || 0,
      "Quantity Received": item.quantityReceived || 0,
      "Quantity Issued": item.quantityIssued || 0,
      "Closing Balance": item.closingBalance || 0,
      "Re-order Level": item.reorderLevel || 0,
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better formatting
    const columnWidths = [
      { wch: 8 }, // Sl No.
      { wch: 20 }, // Service Name
      { wch: 20 }, // Stock Item
      { wch: 15 }, // Opening Balance
      { wch: 18 }, // Quantity Received
      { wch: 15 }, // Quantity Issued
      { wch: 15 }, // Closing Balance
      { wch: 15 }, // Re-order Level
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory Report");

    // Generate filename with date range
    const filename = `Inventory_Report_${formatDate(startDate)}-${formatDate(
      endDate
    )}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h5 className="fw-500 text-white">Inventory Report</h5>
        </div>
      </div>
      <div className="card mb-4">
        <div className="card-header p-4">
          <div className="row">
            <div className="col-md-6 col-sm-6">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-6 col-sm-6">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive mb-4">
            <table className="table table-bordered align-items-center mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="text-sm text-dark">Sl No.</th>
                  <th className="text-sm text-dark">Product Name</th>
                  <th className="text-sm text-dark">Stock Item</th>
                  <th className="text-sm text-dark">Opening Balance</th>
                  <th className="text-sm text-dark">Quantity Received</th>
                  <th className="text-sm text-dark">Quantity Issued</th>
                  <th className="text-sm text-dark">Closing Balance</th>
                  <th className="text-sm text-dark">Re-order Level</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((elem, i) => (
                    <tr key={i}>
                      <td className="text-sm px-4">{i + 1}</td>
                      <td className="text-sm px-4">{elem.parentName}</td>
                      <td className="text-sm px-4">{elem.serviceTypeName}</td>
                      <td className="text-sm px-4">{elem.openingBalance}</td>
                      <td className="text-sm px-4">{elem.quantityReceived}</td>
                      <td className="text-sm px-4">{elem.quantityIssued}</td>
                      <td className="text-sm px-4">{elem.closingBalance}</td>
                      <td className="text-sm px-4">{elem.reorderLevel}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      No Data Found !!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {paginatedData.length > 0 && (
            <nav>
              <ul className="pagination justify-content-center mb-4">
                {renderPagination()}
              </ul>
            </nav>
          )}
          <div className="text-end px-4">
            <button
              className="btn btn-success me-2"
              onClick={() =>
                downloadPDF({
                  title: "Inventory Report",
                  docName: `Inventory_report ${formatDate(
                    startDate
                  )}-${formatDate(endDate)}`,
                  startDate: formatDate(startDate),
                  endDate: formatDate(endDate),
                  rows,
                  columns,
                })
              }
              disabled={
                !isSuccess ||
                isLoading ||
                isFetching ||
                !Array.isArray(Report?.data)
              }
            >
              Download Report <FaRegFilePdf size={20} />
            </button>
            <button
              className="btn btn-warning me-2"
              onClick={downloadExcel}
              disabled={
                !isSuccess ||
                isLoading ||
                isFetching ||
                !Array.isArray(Report?.data)
              }
            >
              Download Excel <FaFileExcel size={20} />
            </button>
            {/* <Link
              to={`print?startDate=${startDate}&endDate=${endDate}`}
              target="_blank"
              disabled={!isSuccess || isLoading || isFetching ||  !Array.isArray(Report?.data)}
            >
              <button
                rel="noopener noreferrer"
                className="btn btn-info"
                disabled={!isSuccess || isLoading || isFetching ||  !Array.isArray(Report?.data)}
              >Print Report</button>
            </Link> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
